import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, ShieldCheck } from "lucide-react";
import { emptyPermissions, PermissionGrid, ROLE_BADGE } from "@/components/OperatorsTab";

/** «Defaults Ρόλων» — μεταφέρθηκε από το tab Χειριστές στα Δικαιώματα (Ιούν 2026). */
export default function RoleDefaultsCard() {
  const [catalog, setCatalog] = useState({ fields: [], roles: [] });
  const [templates, setTemplates] = useState({});
  const [tplDirty, setTplDirty] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cat, tpl] = await Promise.all([
          http.get("/permissions/catalog"),
          http.get("/role-templates"),
        ]);
        setCatalog(cat.data);
        const tplMap = {};
        (tpl.data || []).forEach((t) => { tplMap[t.role] = t.permissions; });
        setTemplates(tplMap);
      } catch {
        toast.error("Σφάλμα φόρτωσης templates ρόλων");
      }
    })();
  }, []);

  const saveTemplate = async (role) => {
    setBusy(true);
    try {
      await http.put(`/role-templates/${role}`, templates[role]);
      toast.success("Defaults ρόλου αποθηκεύτηκαν");
      setTplDirty((d) => ({ ...d, [role]: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const setTplPerms = (role, perms) => {
    setTemplates((t) => ({ ...t, [role]: perms }));
    setTplDirty((d) => ({ ...d, [role]: true }));
  };

  return (
    <Card className="p-5 space-y-4" data-testid="role-defaults-card">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-emerald-600" />
        <h3 className="font-bold text-lg">Defaults Ρόλων</h3>
      </div>
      <p className="text-xs text-slate-600">
        Όταν δημιουργείς νέο χειριστή, τα δικαιώματα γεμίζουν αρχικά από αυτό
        το template. Μπορείς πάντα να τα τροποποιήσεις ανά χρήστη.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {catalog.roles.map((role) => (
          <Card key={role.code} className="p-4 space-y-3 bg-slate-50" data-testid={`role-template-${role.code}`}>
            <div className="flex items-center justify-between">
              <span className={`px-2 py-0.5 rounded text-xs border ${ROLE_BADGE[role.code] || ROLE_BADGE.cashier}`}>
                {role.label}
              </span>
              {tplDirty[role.code] && (
                <Button
                  size="sm"
                  onClick={() => saveTemplate(role.code)}
                  disabled={busy}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid={`role-template-save-${role.code}`}
                >
                  <Save size={14} className="mr-1" />
                  Αποθήκευση
                </Button>
              )}
            </div>
            <PermissionGrid
              value={templates[role.code] || emptyPermissions(catalog.fields)}
              onChange={(p) => setTplPerms(role.code, p)}
              catalog={catalog.fields}
              testidPrefix={`role-template-${role.code}`}
            />
          </Card>
        ))}
      </div>
    </Card>
  );
}
